import ReadWriteFindControl from './ReadWriteFindControl';
import ReadWriteRecipient from './ReadWriteRecipient';
import addReadWriteRecipients from '../actions/addReadWriteRecipients';
import expandGroup from '../actions/expandGroup';
import focusNextRecipient from '../actions/focusNextRecipient';
import removeReadWriteRecipients from '../actions/removeReadWriteRecipients';
import showHideContactPicker from '../actions/showHideContactPicker';
import getRecipientString from '../utils/getRecipientString';
import { observer } from 'mobx-react';
import { ContactPickerPane } from 'owa-contact-picker';
import DraggableRecipient from 'owa-recipient-common/lib/components/DraggableRecipient';
import DroppableWell from 'owa-recipient-common/lib/components/DroppableWell';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import { RecipientType } from 'owa-recipient-common/lib/store/schema/RecipientType';
import removeFindPeopleEntity from 'owa-recipient-common/lib/utils/removeFindPeopleEntity';
import * as React from 'react';
import RecipientWellWithFindControlViewState, {
    ReadWriteRecipientWellSize,
} from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';

let styles = require('./ReadWriteRecipientWellWithFindControl.scss');
let classNames = require('classnames/bind').bind(styles);

export interface ReadWriteRecipientWellWithFindControlProps {
    recipientWell: RecipientWellWithFindControlViewState;
    recipientType: RecipientType;
    size: ReadWriteRecipientWellSize;
    shouldBlockForcedFocus?: boolean;
    onInputFocus?: (evt: React.SyntheticEvent<EventTarget>) => void;
}

@observer
export default class ReadWriteRecipientWellWithFindControl extends React.Component<
    ReadWriteRecipientWellWithFindControlProps,
    any
> {
    private findControl: ReadWriteFindControl;
    private well: HTMLElement;

    public focus() {
        this.findControl.focus();
    }

    private getFindControl = () => {
        return this.well;
    };

    private setContactPickerState = (
        recipientWell: RecipientWellWithFindControlViewState,
        isVisible: boolean
    ) => () => {
        showHideContactPicker(recipientWell, isVisible);
    };

    private addRecipientFromContactPicker = (
        recipientWell: RecipientWellWithFindControlViewState
    ) => (recipientToAdd: FindRecipientPersonaType) => {
        addReadWriteRecipients(
            recipientWell,
            [{ isValid: true, newPersona: recipientToAdd, newEmailAddress: undefined }],
            false /* resetFindResult */
        );
    };

    private bindWellRef = (ref: HTMLElement) => {
        if (ref != null) {
            this.well = ref;
        }
    };

    private removeRecipient = (
        recipientWell: RecipientWellWithFindControlViewState,
        focus: boolean = true
    ) => (recipientToRemove: ReadWriteRecipientViewState, selectToRight?: boolean) => {
        removeFindPeopleEntity(
            this.props.recipientWell.findPeopleFeedbackManager,
            this.props.recipientWell.recipients[length - 1]
        );
        removeReadWriteRecipients(recipientWell, [recipientToRemove], selectToRight);
        if (focus && recipientWell.recipients.length == 0) {
            this.focus();
        }
    };

    private expandGroup = (recipientWell: RecipientWellWithFindControlViewState) => (
        recipientToExpand: ReadWriteRecipientViewState
    ) => {
        expandGroup(recipientWell, recipientToExpand);
    };

    private focusNextRecipient = (recipientWell: RecipientWellWithFindControlViewState) => (
        recipientToBlur: ReadWriteRecipientViewState,
        direction: number
    ) => {
        if (
            recipientWell.recipients.indexOf(recipientToBlur) + direction >=
            recipientWell.recipients.length
        ) {
            this.findControl.focus();
        } else {
            focusNextRecipient(recipientWell, recipientToBlur, direction);
        }
    };

    private addDroppedRecipient = (recipient: ReadWriteRecipientViewState) => {
        addReadWriteRecipients(
            this.props.recipientWell,
            [
                {
                    isValid: recipient.isValid,
                    newPersona: recipient.persona,
                    newEmailAddress: undefined,
                },
            ],
            false /* resetFindResult */
        );
    };

    render() {
        let {
            recipientWell,
            recipientType,
            size,
            shouldBlockForcedFocus,
            onInputFocus,
        } = this.props;
        if (recipientWell) {
            let wellLabel =
                recipientType != RecipientType.None ? (
                    <div
                        tabIndex={0}
                        role={'button'}
                        onClick={this.setContactPickerState(
                            recipientWell,
                            !recipientWell.shouldShowContactPicker
                        )}
                        className={styles.label}>
                        {getRecipientString(recipientType)}
                    </div>
                ) : null;
            let recipients = recipientWell.recipients;
            return (
                <DroppableWell
                    dropViewState={this.props.recipientWell.dropViewState}
                    addRecipientAction={this.addDroppedRecipient}>
                    <div className={styles.container} ref={this.bindWellRef}>
                        {wellLabel}
                        {recipientWell.shouldShowContactPicker && (
                            <ContactPickerPane
                                findControlGetter={this.getFindControl}
                                dismissContactPickerAction={this.setContactPickerState(
                                    recipientWell,
                                    false /* isVisible */
                                )}
                                addRecipientAction={this.addRecipientFromContactPicker(
                                    recipientWell
                                )}
                            />
                        )}
                        {recipients && recipients.length > 0
                            ? recipients.map((recipient, index) => {
                                  // Using an index as part of the key is sub-optimal, but the recipient array
                                  // can contain duplicate data that has no other means of differentiating.
                                  return (
                                      <DraggableRecipient
                                          key={`${
                                              recipient.persona.EmailAddress.EmailAddress
                                          }_${index}`}
                                          dragViewState={recipient.drag}
                                          recipientType={this.props.recipientType}
                                          recipient={recipient}
                                          removeRecipientAction={this.removeRecipient(
                                              recipientWell,
                                              false /*focus*/
                                          )}>
                                          <ReadWriteRecipient
                                              viewState={recipient}
                                              index={index}
                                              removeOperation={this.removeRecipient(recipientWell)}
                                              focusNextOperation={this.focusNextRecipient(
                                                  recipientWell
                                              )}
                                              size={size}
                                              expandGroupOperation={
                                                  size == ReadWriteRecipientWellSize.Regular &&
                                                  this.expandGroup(recipientWell)
                                              }
                                              useContextMenu={true}
                                              isSelected={recipient.isSelected}
                                          />
                                      </DraggableRecipient>
                                  );
                              })
                            : null}
                        <ReadWriteFindControl
                            recipientWell={recipientWell}
                            size={size}
                            className={classNames(styles.findControl, {
                                smallSize: size == ReadWriteRecipientWellSize.Small,
                                regularSize: size == ReadWriteRecipientWellSize.Regular,
                            })}
                            shouldBlockForcedFocus={shouldBlockForcedFocus}
                            onInputFocus={onInputFocus}
                            ref={ref => (this.findControl = ref)}
                        />
                    </div>
                </DroppableWell>
            );
        } else {
            return null;
        }
    }
}
